import { useAccount, useReadContract, useReadContracts } from "wagmi";
import useDappConfig from "./useDappConfig";
import { formatEther } from "viem";
import useExchangeRate from "./useExchangeRate";
import { MINT_LIMIT_CHAINS, ChainId } from "@constants/chains";

const useMintLimit = () => {
  const { config } = useDappConfig();
  const { exchangeRate } = useExchangeRate();
  const { chainId } = useAccount();

  const isEthereum = chainId === ChainId.ETHEREUM;

  // For Ethereum: fetch both maxMintAmountPerDay and currentPeriodMintedAmount
  const { data: ethereumData, isLoading: isLoadingEthereum } = useReadContracts(
    {
      contracts: [
        {
          ...config.xtoken,
          functionName: "maxMintAmountPerDay",
        },
        {
          ...config.xtoken,
          functionName: "currentPeriodMintedAmount",
        },
      ],
      query: {
        enabled: !!chainId && isEthereum && MINT_LIMIT_CHAINS.includes(chainId),
        refetchInterval: 60_000,
      },
    }
  );

  // For L2s: use the existing remainingDailyMintLimit function
  const {
    data: l2Data,
    isLoading: isLoadingL2,
    isError: isErrorL2,
  } = useReadContract({
    ...config.interactionPools.lrtDepositPool,
    functionName: "remainingDailyMintLimit",

    query: {
      enabled: !!chainId && !isEthereum && MINT_LIMIT_CHAINS.includes(chainId),
      refetchInterval: 60_000,
    },
  });

  // Early return for loading or invalid chain
  if (!chainId || !MINT_LIMIT_CHAINS.includes(chainId)) {
    return {
      remainingLimit: null,
      remainingLimitRsEth: null,
      isLoading: false,
      isError: null,
    };
  }

  if ((isEthereum && isLoadingEthereum) || (!isEthereum && isLoadingL2)) {
    return {
      remainingLimit: null,
      remainingLimitRsEth: null,
      isLoading: true,
      isError: null,
    };
  }

  let rsEthMintLimit: bigint;

  if (isEthereum) {
    // For Ethereum: calculate remainingDailyMintLimit = maxMintAmountPerDay - currentPeriodMintedAmount
    if (
      !ethereumData ||
      ethereumData[0].status !== "success" ||
      ethereumData[1].status !== "success"
    ) {
      return {
        remainingLimit: null,
        remainingLimitRsEth: null,
        isLoading: false,
        isError: true,
      };
    }

    const maxMintAmount = ethereumData[0].result as bigint;
    const currentMinted = ethereumData[1].result as bigint;
    rsEthMintLimit = maxMintAmount - currentMinted;
  } else {
    // For L2s: use the direct remainingDailyMintLimit function
    if (!l2Data || isErrorL2) {
      return {
        remainingLimit: null,
        remainingLimitRsEth: null,
        isLoading: false,
        isError: true,
      };
    }
    rsEthMintLimit = l2Data as bigint;
  }

  const remainingLimit = Number(rsEthMintLimit) * (exchangeRate || 1);
  const formattedValue = Number(formatEther(BigInt(remainingLimit))).toFixed(2);
  const formattedValueRsEth = Number(formatEther(rsEthMintLimit)).toFixed(2);

  return {
    remainingLimit: formattedValue,
    remainingLimitRsEth: formattedValueRsEth,
    isLoading: false,
    isError: null,
  };
};

export default useMintLimit;
